/* eslint-disable i18next/no-literal-string */
import { createFileRoute } from '@tanstack/react-router'

import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Badge } from '@/components/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { Dropdown } from '@/components/Dropdown'
import { NewDropdown } from '@/components/NewDropdown'
import type { NewDropdownOption } from '@/components/NewDropdown/types'
import { INDETERMINATE } from '@repo/constants'
import {
  Button,
  Checkbox,
  Input,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ui/components'
import { AlertCircleIcon, ClockAlertIcon, ListFilterIcon, Settings2Icon } from 'lucide-react'
import { useState } from 'react'

import { AlertDialog } from '@/components/AlertDialog'
import { DeleteAlertDialog } from '@/components/AlertDialog/PresetAlerts/DeleteAlertDialog'
import { PublishAlertDialog } from '@/components/AlertDialog/PresetAlerts/PublishAlertDialog'
import { CodeBlock } from '@/components/CodeBlock'
import { GuideHelper } from '@/components/GuideHelper'
import { MultiSelect, MultiSelectOption } from '@/components/MultiSelect'
import { toast } from 'sonner'

export const Route = createFileRoute('/_auth/_modules/_shared/kitchen-sink')({
  component: RouteComponent,
})

const dropdownOptions = [
  { label: 'Apple', value: 'apple' },
  { label: 'Banana', value: 'banana' },
  { label: 'Orange', value: 'orange' },
  { label: 'Strawberry', value: 'strawberry' },
  { label: 'Grape', value: 'grape' },
  { label: 'Mango', value: 'mango' },
  { label: 'Pineapple', value: 'pineapple' },
  { label: 'Blueberry', value: 'blueberry' },
  { label: 'Peach', value: 'peach' },
  { label: 'Pear', value: 'pear' },
  { label: 'Plum', value: 'plum' },
  { label: 'Cherry', value: 'cherry' },
  { label: 'Kiwi', value: 'kiwi' },
  { label: 'Watermelon', value: 'watermelon' },
  { label: 'Raspberry', value: 'raspberry' },
  { label: 'Blackberry', value: 'blackberry' },
  { label: 'Pomegranate', value: 'pomegranate' },
  { label: 'Lemon', value: 'lemon' },
  { label: 'Lime', value: 'lime' },
  { label: 'Coconut', value: 'coconut' },
]

// Convert dropdownOptions to NewDropdownOption format
const newDropdownOptions: NewDropdownOption[] = dropdownOptions.map((option) => ({
  label: option.label,
  value: option.value,
  keywords: [option.label.toLowerCase(), option.value.toLowerCase()],
}))

const placeholderJSONCode = `{
  "name": "John Doe",
  "age": 30,
  "email": "john.doe@example.com",
  "address": {
    "street": "123 Main St",
    "city": "Anytown",
    "state": "CA",
    "zipCode": "12345",
    "country": "USA"
  },
  "phoneNumbers": [
    {
      "type": "home",
      "number": "555-1234"
    },
    {
      "type": "work",
      "number": "555-5678"
    },
    {
      "type": "mobile",
      "number": "555-9012"
    }
  ],
  "education": [
    {
      "institution": "University of Example",
      "degree": "Bachelor of Science",
      "fieldOfStudy": "Computer Science",
      "startYear": 2010,
      "endYear": 2014,
      "gpa": 3.8
    },
    {
      "institution": "Graduate Institute",
      "degree": "Master of Science",
      "fieldOfStudy": "Data Science",
      "startYear": 2015,
      "endYear": 2017,
      "gpa": 3.9
    }
  ],
  "workExperience": [
    {
      "company": "Tech Solutions Inc.",
      "position": "Software Developer",
      "startDate": "2017-06-01",
      "endDate": "2019-12-31",
      "responsibilities": [
        "Developed web applications",
        "Maintained legacy code",
        "Collaborated with cross-functional teams"
      ]
    },
    {
      "company": "Data Insights Corp",
      "position": "Senior Developer",
      "startDate": "2020-01-15",
      "endDate": null,
      "current": true,
      "responsibilities": [
        "Lead development team",
        "Architect solutions",
        "Implement CI/CD pipelines"
      ]
    }
  ],
  "skills": [
    "JavaScript",
    "TypeScript",
    "React",
    "Node.js",
    "Python",
    "SQL",
    "Git",
    "Docker",
    "AWS",
    "Machine Learning"
  ],
  "languages": [
    {
      "name": "English",
      "proficiency": "Native"
    },
    {
      "name": "Spanish",
      "proficiency": "Intermediate"
    },
    {
      "name": "French",
      "proficiency": "Beginner"
    }
  ],
  "projects": [
    {
      "name": "E-commerce Platform",
      "description": "Built a full-stack e-commerce solution",
      "technologies": ["React", "Node.js", "MongoDB"],
      "url": "https://github.com/johndoe/ecommerce"
    },
    {
      "name": "Data Visualization Dashboard",
      "description": "Created interactive data visualizations",
      "technologies": ["D3.js", "React", "Python"],
      "url": "https://github.com/johndoe/data-viz"
    }
  ],
  "socialProfiles": {
    "github": "https://github.com/johndoe",
    "linkedin": "https://linkedin.com/in/johndoe",
    "twitter": "https://twitter.com/johndoe"
  },
  "preferences": {
    "theme": "dark",
    "notifications": true,
    "newsletter": false,
    "twoFactorAuth": true
  }
}`

function RouteComponent() {
  const [multiSelectValue, setMultiSelectValue] = useState<MultiSelectOption[]>([])
  const [dropdownValue, setDropdownValue] = useState<string | undefined>(undefined)
  const [checkboxValue, setCheckboxValue] = useState<boolean | 'indeterminate'>(false)

  // NewDropdown state
  const [singleSelectValue, setSingleSelectValue] = useState<string>('')
  const [multiSelectNewValue, setMultiSelectNewValue] = useState<string[]>([])
  const [searchableValue, setSearchableValue] = useState<string[]>([])

  const [alertOpen, setAlertOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [actionStarted, setActionStarted] = useState(false)

  const handlePublish = () => {
    setActionStarted(true)

    setTimeout(() => {
      setActionStarted(false)
      toast.success('Action completed')
    }, 5000)
  }

  return (
    <PageContainer>
      <Spread>
        <PageHeader>Kitchen Sink</PageHeader>
        <HStack gap="sm">
          <Button>Click me</Button>
          <Button
            variant="destructive"
            onClick={() => {
              throw new Error('This is your first error!')
            }}
          >
            Break the world!
          </Button>
        </HStack>
      </Spread>

      <Card>
        <CardHeader>
          <CardTitle>
            Dropdown Refactor
            <GuideHelper guideUrl="/" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <VStack gap="lg">
            {/* Basic Single Select */}
            <VStack gap="sm">
              <h3 className="text-lg font-semibold">Basic Single Select</h3>
              <NewDropdown
                label="Select an option"
                options={newDropdownOptions}
                placeholder="Choose an option..."
                value={singleSelectValue}
                onChange={setSingleSelectValue}
                style={{
                  triggerClassName: 'w-80',
                  popoverClassName: 'w-80',
                }}
              />
              <p className="text-muted-foreground text-sm">
                Selected: {singleSelectValue || 'None'}
              </p>
            </VStack>

            <Separator />

            {/* Single Select with Unselect Allowed */}
            <VStack gap="sm">
              <h3 className="text-lg font-semibold">Single Select with Unselect Allowed</h3>
              <NewDropdown
                label="Single select with unselect allowed"
                options={newDropdownOptions}
                selectionMode="single"
                allowUnselectInSingleMode={true}
                text={{
                  triggerPlaceholder: 'Select an option...',
                }}
                value={singleSelectValue}
                onChange={setSingleSelectValue}
                style={{
                  triggerClassName: 'w-80',
                  popoverClassName: 'w-80',
                }}
              />
              <p className="text-muted-foreground text-sm">
                Selected: {singleSelectValue || 'None'} (click selected option to unselect)
              </p>
            </VStack>

            <Separator />

            {/* Single Select with Search */}
            <VStack gap="sm">
              <h3 className="text-lg font-semibold">Single Select with Search</h3>
              <NewDropdown
                label="Searchable single select"
                options={newDropdownOptions}
                selectionMode="single"
                searchable={true}
                text={{
                  triggerPlaceholder: 'Search and select...',
                  searchPlaceholder: 'Type to search...',
                  emptyResult: 'No matching options found',
                }}
                value={singleSelectValue}
                onChange={setSingleSelectValue}
                style={{
                  triggerClassName: 'w-80',
                  popoverClassName: 'w-80',
                }}
              />
              <p className="text-muted-foreground text-sm">
                Selected: {singleSelectValue || 'None'}
              </p>
            </VStack>

            <Separator />

            {/* Multi Select */}
            <VStack gap="sm">
              <h3 className="text-lg font-semibold">Multi Select</h3>
              <NewDropdown
                label="Select multiple options"
                options={newDropdownOptions}
                selectionMode="multiple"
                text={{
                  triggerPlaceholder: 'Select options...',
                  selectedTextPrefix: { singular: 'option', plural: 'options' },
                }}
                value={multiSelectNewValue}
                onChange={setMultiSelectNewValue}
                style={{
                  triggerClassName: 'w-80',
                  popoverClassName: 'w-80',
                }}
              />
              <p className="text-muted-foreground text-sm">
                Selected: {multiSelectNewValue.length > 0 ? multiSelectNewValue.join(', ') : 'None'}
              </p>
            </VStack>

            <Separator />

            {/* Multi Select with Hidden Header */}
            <VStack gap="sm">
              <h3 className="text-lg font-semibold">Multi Select without Header</h3>
              <NewDropdown
                label="Multi select without header"
                options={newDropdownOptions}
                selectionMode="multiple"
                hideSelectionHeader={true}
                text={{
                  triggerPlaceholder: 'Select options...',
                  selectedTextPrefix: { singular: 'option', plural: 'options' },
                }}
                value={multiSelectNewValue}
                onChange={setMultiSelectNewValue}
                style={{
                  triggerClassName: 'w-80',
                  popoverClassName: 'w-80',
                }}
              />
              <p className="text-muted-foreground text-sm">
                Selected: {multiSelectNewValue.length > 0 ? multiSelectNewValue.join(', ') : 'None'}
              </p>
            </VStack>

            <Separator />

            {/* Searchable with Hidden Footer */}
            <VStack gap="sm">
              <h3 className="text-lg font-semibold">Searchable without Footer</h3>
              <NewDropdown
                label="Searchable without footer"
                options={newDropdownOptions}
                selectionMode="multiple"
                searchable={true}
                hideSearchFooter={true}
                text={{
                  triggerPlaceholder: 'Search and select...',
                  searchPlaceholder: 'Type to search...',
                  emptyResult: 'No matching options found',
                }}
                value={searchableValue}
                onChange={setSearchableValue}
                style={{
                  triggerClassName: 'w-80',
                  popoverClassName: 'w-80',
                }}
              />
              <p className="text-muted-foreground text-sm">
                Selected: {searchableValue.length > 0 ? searchableValue.join(', ') : 'None'}
              </p>
            </VStack>

            <Separator />

            {/* Loading and Disabled States and custom styling */}
            <VStack gap="sm">
              <h3 className="text-lg font-semibold">States and custom styling</h3>
              <HStack gap="md">
                <NewDropdown
                  label="Loading state"
                  options={newDropdownOptions}
                  loading={true}
                  placeholder="Loading options..."
                  value=""
                  onChange={() => {}}
                />
                <NewDropdown
                  label="Disabled state"
                  options={newDropdownOptions}
                  disabled={true}
                  placeholder="This dropdown is disabled"
                  value=""
                  onChange={() => {}}
                />
                <NewDropdown
                  label="Custom styled dropdown"
                  options={newDropdownOptions}
                  selectionMode="multiple"
                  searchable={true}
                  style={{
                    triggerClassName: 'w-80 bg-blue-50 border-blue-200 hover:bg-blue-100',
                    popoverClassName: 'w-24',
                  }}
                  text={{
                    triggerPlaceholder: 'Custom placeholder...',
                    searchPlaceholder: 'Custom search...',
                  }}
                  value={[]}
                  onChange={() => {}}
                />
              </HStack>
            </VStack>
          </VStack>
        </CardContent>
      </Card>

      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}

      <Card>
        <CardHeader>
          <CardTitle>Alert Dialog</CardTitle>
        </CardHeader>
        <CardContent>
          <HStack gap="md">
            <Button onClick={() => setAlertOpen(true)}>Open Publish Dialog</Button>

            <Button onClick={() => setDeleteOpen(true)}>Open Delete Dialog</Button>

            <AlertDialog
              title="Simple Alert Dialog"
              description="This is a simple alert dialog. It is used for acknowledging an action."
              trigger={<Button>Open Simple Alert Dialog</Button>}
              action={<Button>Ok</Button>}
              hideCancel
            />
          </HStack>

          <DeleteAlertDialog
            title="Delete This Thing"
            description="Oh no! This thing is important. Are you sure you want to delete it?"
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            onDelete={() => {
              toast.success('Deleted')
              setDeleteOpen(false)
            }}
          />

          <PublishAlertDialog
            title="Publish All Launches"
            warning="This will overwrite all of the current launch data in the production environment with the current data."
            open={alertOpen}
            onOpenChange={setAlertOpen}
            onPublish={handlePublish}
            isPending={actionStarted}
          />
        </CardContent>
      </Card>

      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}

      <Card>
        <CardHeader>
          <CardTitle>Code Block</CardTitle>
        </CardHeader>
        <CardContent>
          <CodeBlock
            code={placeholderJSONCode}
            language="json"
            title="Error"
            titleIcon={AlertCircleIcon}
            className="max-h-[300px]"
            theme="dark"
            showLineNumbers={true}
          />
          <CodeBlock
            showLineNumbers={true}
            wrapLines={true}
            code={`File couldn't process as headers are not as expected: ['tax_list', 'sku_type', 'sku_category', 'tax_rate1', 'tax_rate2'], Current: ['active_flag', 'sku', 'max'].
This is another line of code.
And another one.`}
          />
        </CardContent>
      </Card>

      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}

      <Card>
        <CardHeader>
          <CardTitle>Order Data</CardTitle>
        </CardHeader>

        <CardContent>
          <VStack gap="md">
            <Spread>
              <HStack gap="sm">
                <Input placeholder="Search orders..." />
                <Button>
                  <ListFilterIcon />
                  Filter
                </Button>
              </HStack>

              <Button variant="secondary">
                <Settings2Icon />
                Columns
              </Button>
            </Spread>

            <span>table</span>

            <HStack gap="sm">
              <Badge>Default</Badge>
              <Badge tone="critical">
                <ClockAlertIcon className="-ml-1 size-4" strokeWidth={2.5} />
                Off Route
              </Badge>
              <Badge tone="success">Success</Badge>
              <Badge tone="warning">Warning</Badge>
              <Badge tone="info">Info</Badge>
            </HStack>

            <HStack gap="sm">
              <Badge border>Default</Badge>
              <Badge tone="critical" border>
                Critical
              </Badge>
              <Badge tone="success" border>
                Success
              </Badge>
              <Badge tone="warning" border>
                Warning
              </Badge>
              <Badge tone="info" border>
                Info
              </Badge>
            </HStack>

            <HStack gap="sm">
              <Badge inverted>Default</Badge>
              <Badge tone="critical" inverted>
                Critical
              </Badge>
              <Badge tone="success" inverted>
                Success
              </Badge>
              <Badge tone="warning" inverted>
                Warning
              </Badge>
              <Badge tone="info" inverted>
                Info
              </Badge>
            </HStack>
          </VStack>
        </CardContent>
      </Card>

      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}

      <Card>
        <CardHeader>
          <CardTitle>Multi Select</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-28">
            <MultiSelect
              options={dropdownOptions}
              value={multiSelectValue}
              onChange={setMultiSelectValue}
            />
          </div>
        </CardContent>
      </Card>

      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}

      <Card>
        <CardHeader>
          <CardTitle>Dropdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Dropdown
            options={dropdownOptions}
            value={dropdownValue}
            onChange={setDropdownValue}
            text={{ triggerPlaceholder: 'Select an option' }}
          />

          <Sheet>
            <SheetTrigger asChild>
              <Button>open sheet</Button>
            </SheetTrigger>

            <SheetContent className="w-[512px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>

              <VStack gap="md" className="p-6">
                <Dropdown
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  text={{ triggerPlaceholder: 'Select an option' }}
                />
              </VStack>
            </SheetContent>
          </Sheet>
        </CardContent>
      </Card>

      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}

      <Card>
        <CardHeader>
          <CardTitle>Checkboxes</CardTitle>
        </CardHeader>
        <CardContent>
          <HStack gap="md">
            <Checkbox checked={true} />
            <Checkbox checked={false} />
            <Checkbox checked={'indeterminate'} />
            <Separator orientation="vertical" className="h-2" />
            <Checkbox
              checked={checkboxValue}
              onCheckedChange={() => {
                if (checkboxValue === false) {
                  setCheckboxValue(true)
                } else if (checkboxValue === true) {
                  setCheckboxValue(INDETERMINATE)
                } else {
                  setCheckboxValue(false)
                }
              }}
            />
          </HStack>
        </CardContent>
      </Card>

      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}
      {/* ////////////////////////////////////////////////////////////// */}

      <Card>
        <CardHeader>
          <CardTitle>Toasters</CardTitle>
        </CardHeader>
        <CardContent>
          <HStack gap="md">
            <Button
              onClick={() =>
                toast('Hello', {
                  description: 'This is a description',
                  action: {
                    label: 'Click me',
                    onClick: () => alert('clicked'),
                  },
                })
              }
            >
              Toast Default
            </Button>
            <Button
              onClick={() =>
                toast.success('Hello', {
                  description: 'This is a description',
                  action: {
                    label: 'Click me',
                    onClick: () => alert('clicked'),
                  },
                })
              }
            >
              Toast Success
            </Button>
            <Button
              onClick={() =>
                toast.error('Hello', {
                  description: 'This is a description',
                  action: {
                    label: 'Click me',
                    onClick: () => alert('clicked'),
                  },
                })
              }
            >
              Toast Critical
            </Button>
            <Button
              onClick={() =>
                toast.warning('Hello', {
                  description: 'This is a description',
                  action: {
                    label: 'Click me',
                    onClick: () => alert('clicked'),
                  },
                })
              }
            >
              Toast Warning
            </Button>

            <Button
              onClick={() =>
                toast.info('Hello', {
                  description: 'This is a description',
                  action: {
                    label: 'Click me',
                    onClick: () => alert('clicked'),
                  },
                })
              }
            >
              Toast Info
            </Button>
          </HStack>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
